import { 
    Viewer, 
    Event, 
    LabelCollection, 
    PointPrimitiveCollection, 
    PolylineCollection, 
    PrimitiveCollection, 
    Cartesian3, 
    Color, 
    CallbackProperty, 
    PointPrimitive,
    ScreenSpaceEventHandler,
    ScreenSpaceEventType,
    VerticalOrigin,
    Math,
    HorizontalOrigin,
    Cartesian2,
    Polyline,
    Material,
    PolylineOutlineMaterialProperty
} from "cesium"

import * as turf from '@turf/turf'

export default class MeasureTool {

    /**
     * 
     * @param {Viewer} viewer 
     * @param {*} options 
     * @param {String} unit 长度单位 km 千米  nmi 海里
     */
    constructor(viewer, options) {
        this.options = Object.assign({unit: 'km'}, options)
        this.viewer = viewer
        this.initPrimitive(this.viewer.scene.primitives)
        this.status = 'finish'
        this.hasPoint = false
        this.moveText = '点击开始测量'
        this.linePoints = [] // 线的点position数据
        this.measureLinePoints = [] // 用于计算长度的点
        this.closeBillbord = null // 关闭测量线的按钮
        this.moveLabel = null // 跟随鼠标移动的实体
        this.line = null // 线实体
        this.labels = null // 存储显示测量长度的label的collection
        this.events = {
            finish: new Event(),
            node: new Event()
        }
        this.handler = new ScreenSpaceEventHandler(this.viewer.scene.canvas);
    }

    addEvent() {
       
        this.handler.setInputAction((event) => {
            if(this.status === 'finish') {
                return
            }
            const lnglat = this.getLngLatByPosition(event.endPosition)
            if(!lnglat) {
                return
            }
            const {lng, lat} = lnglat
            const position = Cartesian3.fromDegrees(lng, lat, 0)
            if(this.moveLabel) {
                this.moveLabel.position = new CallbackProperty(() => {
                    return position
                }, false)
            }else{
                this.moveLabel = this.viewer.entities.add({
                    id: 'measure-move-label',
                    position: position,
                    label: {
                        text: this.moveText,
                        font: '12px sans-serif',
                        verticalOrigin: VerticalOrigin.TOP,
                        horizontalOrigin: HorizontalOrigin.LEFT,
                        pixelOffset: new Cartesian2(12, 0)
                    },
                    point: {
                        pixelSize: 5,
                        color: Color.WHITE,
                        outlineColor: Color.RED,
                        outlineWidth: 2
                    }
                })
            }
            this.editorLineLastPoint(position)
        }, ScreenSpaceEventType.MOUSE_MOVE)

        this.handler.setInputAction((event) => {
            if(this.status === 'finish') {
                const pick = this.viewer.scene.pick(event.position)
                if(pick && pick.id && pick.id.id === 'measure-close') {
                    this.reset()
                }
                return
            }
            const lngLat = this.getLngLatByPosition(event.position)
            this.hasPoint = true
            this.moveText = '右键完成测量'
            this.moveLabel.label.text = new CallbackProperty(() => {
                return this.moveText
            }, false)
            this.addNode(lngLat)
        }, ScreenSpaceEventType.LEFT_CLICK)

        this.handler.setInputAction((event) => {
            const lngLat = this.getLngLatByPosition(event.position)
            this.hasPoint = false
            this.status = 'finish'
            this.viewer.entities.remove(this.moveLabel)
            this.removeLastMovePosition()
            this.addCloseBillbord()
            this.events.finish.raiseEvent({target: {
                status: 'finish',
                lngLat: lngLat,
                type: this.type
            }})
        }, ScreenSpaceEventType.RIGHT_CLICK)
    }

    removeEvent() {
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE)
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK)
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK)
    }

    addCloseBillbord() {
        const position = this.linePoints[this.linePoints.length - 1]
        this.closeBillbord = this.viewer.entities.add({
            id: 'measure-close',
            position: position,
            label: {
                text: 'x',
                fillColor: Color.RED,
                outlineWidth: 0,
                font: '15px sans-serif',
                pixelOffset: new Cartesian2(0, -20)
            }
        })
    }

    removeLastMovePosition() {
        if(this.linePoints.length > 2) {
            this.linePoints.splice(-1, 1)
            if(this.line) {
                this.line.polyline.positions = new CallbackProperty(() => {
                    return this.linePoints
                }, false)
            }
        }else if(this.linePoints.length == 2) {
            this.reset()
        }
    }

    editorLineLastPoint(position) {
        if(this.linePoints.length > 1) {
            this.linePoints.splice(-1, 1, position)
            if(this.line) {
                this.line.polyline.positions = new CallbackProperty(() => {
                    return this.linePoints
                }, false)
            }
        }else if(this.linePoints.length == 1) {
            this.linePoints.push(position)
            this.line = this.viewer.entities.add({
                polyline: {
                    width: 2,
                    material: Color.RED,
                    positions: this.linePoints
                }
            })
        }
    }

    initPrimitive(primitives) {
        this.primitives = primitives.add(new PrimitiveCollection())
    }

    clear() {
        if(this.labels) {
            this.labels.removeAll()
        }else {
            this.labels = this.primitives.add(new LabelCollection())
        }

        if(this.points) {
            this.points.removeAll()
        }else{
            this.points = this.primitives.add(new PointPrimitiveCollection())
        }
        if(this.moveLabel) {
            if(this.viewer.entities.getById('measure-move-label')) {
                this.viewer.entities.remove(this.moveLabel)
            }else {
                this.moveLabel = null
            }
        }
    }

    // 添加节点
    addNode(lngLat) {
        let {lng, lat} = lngLat
        const position = Cartesian3.fromDegrees(lng, lat, 0)
        let point = new PointPrimitive()
        point.color = Color.WHITE.withAlpha(0.7)
        point.outlineColor = Color.RED
        point.outlineWidth = 2
        point.position = position
        point.pixelSize = 5
        this.points.add(point)
        this.measureLinePoints.push([lng, lat])
        let content = '起点'
        if(this.measureLinePoints.length > 1) {
            const line = turf.lineString(this.measureLinePoints)
            const length = turf.length(line, {units: 'kilometers'})
            if(this.options.unit === 'nmi') {
                const nmLength = length / 1.852
                content = nmLength.toFixed(2).concat('nm')
            }else {
                content = length.toFixed(2).concat('km')
            }
        }
        this.labels.add({
            position: position,
            text: content,
            font: '24px sans-serif',
            fillColor: Color.WHITE,
            scale: 0.5,
            showBackground: true,
            backgroundPadding: new Cartesian2(5, 5),
            horizontalOrigin: HorizontalOrigin.LEFT,
            verticalOrigin: VerticalOrigin.CENTER,
            pixelOffset: new Cartesian2(10, 0)

        })
        this.linePoints.push(position)
    }

    setType(type) {
        this.type = type
    }

    start() {
        this.status = 'start'
        this.moveText = '点击开始测量'
        this.reset()
        this.addEvent()
    }

    reset() {
        this.removeEvent()
        this.clear()
        this.viewer.entities.remove(this.closeBillbord)
        this.measureLinePoints = []
        this.linePoints = []
        this.hasPoint = false
    }

    getLngLatByPosition(position) {
        if(position) {
            let cartesian  = this.viewer.camera.pickEllipsoid(
                position,
                this.viewer.scene.globe.ellipsoid
            )
            if(cartesian) {
                let cartographic = this.viewer.scene.globe.ellipsoid.cartesianToCartographic(cartesian)
                return {
                    lng: Math.toDegrees(cartographic.longitude),
                    lat: Math.toDegrees(cartographic.latitude)
                }
            }
        }
        return null
    }

    on(event, callback) {
        if(this.events[event]) {
            this.events[event].addEventListener(event => {
                callback(event)
            })
        }
    }
}